/**
 * Shared formatters for booking, chat and loyalty payloads.
 *
 * The API is inconsistent: some endpoints return a bare array/object, others
 * wrap it in `{ data: ... }`, paginated ones nest it further, and Laravel sends
 * `decimal` columns as strings ("20.00"). These helpers normalise all of that
 * so pages never have to guess.
 */

/** Returns the list inside a response body, unwrapping any `data` envelope. */
export const extractList = (body) => {
  if (Array.isArray(body)) return body;
  if (body && typeof body === 'object' && Array.isArray(body.data)) return body.data;
  return [];
};

/** Returns the object inside a response body, unwrapping a `data` envelope. */
export const extractObject = (body) => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) return body.data;
  return body;
};

/** Reads a number that the API may send as a number or a decimal string. */
export const toNumber = (value, fallback = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
};

/**
 * Formats a `YYYY-MM-DD` booking date without the UTC off-by-one that
 * `new Date('2026-09-28')` causes (it is parsed as UTC midnight, so users
 * behind UTC see the previous day).
 */
export const formatBookingDate = (value) => {
  if (!value) return '-';

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};

/**
 * Formats a booking time. Accepts `HH:MM`, `HH:MM:SS` and the full ISO
 * timestamps older API responses returned for the time-only column.
 */
export const formatBookingTime = (value) => {
  if (!value) return '-';

  const match = /(\d{1,2}):(\d{2})/.exec(String(value));
  if (!match) return String(value);

  const hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${hour12}:${minutes} ${suffix}`;
};

/** Formats a full ISO timestamp, e.g. "26 Sep 2026, 2:04 PM". */
export const formatDateTime = (value) => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return `${parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}, ${parsed.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}`;
};

/** Formats an amount as NPR, trimming a trailing `.00`. */
export const formatMoney = (value) => {
  const amount = toNumber(value, 0);
  const formatted = Number.isInteger(amount)
    ? amount.toLocaleString('en-US')
    : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return `NPR ${formatted}`;
};

/**
 * Builds a single-line address for a booking.
 *
 * Prefers the structured `customer_address` the provider endpoint attaches,
 * then the cleaned `address` snapshot, and always drops empty segments so a
 * stored value like ", Lalitpur, Bagmati, " never reaches the UI.
 */
export const formatAddress = (booking) => {
  if (!booking) return 'Not specified';

  const structured = booking.customer_address;
  if (structured && typeof structured === 'object') {
    const parts = [
      structured.address_line1,
      structured.address_line2,
      structured.landmark,
      structured.city,
      structured.state,
      structured.country,
    ]
      .map((part) => (typeof part === 'string' ? part.trim() : part))
      .filter(Boolean);

    if (parts.length) return [...new Set(parts)].join(', ');
  }

  const snapshot = typeof booking.address === 'string' ? booking.address : '';
  const parts = snapshot
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length ? parts.join(', ') : 'Not specified';
};
